import { Gift } from "lucide-react";
import { loadImg, iconImg, roundRect, text, drawBackground, drawBrand, drawCta, drawAvatar } from "@/lib/badgeImage";

const newCanvas = (W, H) => { const c = document.createElement("canvas"); c.width = W; c.height = H; return [c, c.getContext("2d")]; };
const toBlob = (c) => new Promise((resolve) => c.toBlob((b) => resolve(b), "image/png"));

const whiteCard = (ctx, x, y, w, h, r) => {
  ctx.save(); ctx.shadowColor = "rgba(131,24,67,0.4)"; ctx.shadowBlur = 60; ctx.shadowOffsetY = 22;
  roundRect(ctx, x, y, w, h, r); ctx.fillStyle = "#fff"; ctx.fill(); ctx.restore();
};

const pricePill = (ctx, cx, y, label, size) => {
  ctx.font = `900 ${size}px "Outfit", sans-serif`;
  const w = ctx.measureText(label).width + size * 1.4, h = size * 1.55;
  const g = ctx.createLinearGradient(cx - w / 2, 0, cx + w / 2, 0); g.addColorStop(0, "#FBBF24"); g.addColorStop(1, "#F97316");
  ctx.save(); ctx.shadowColor = "rgba(180,83,9,0.45)"; ctx.shadowBlur = 30; ctx.shadowOffsetY = 10;
  roundRect(ctx, cx - w / 2, y, w, h, h / 2); ctx.fillStyle = g; ctx.fill(); ctx.restore();
  text(ctx, label, cx, y + h * 0.7, { size, weight: 900, color: "#fff" });
};

const GIFT_L = {
  story: { W: 1080, H: 1920, logo: 150, label: 330, card: [140, 400, 800, 800], title: 1330, price: 1400, cta: [1620, 240] },
  square: { W: 1080, H: 1080, logo: 80, label: 170, card: [290, 210, 500, 440], title: 730, price: 770, cta: [900, 150] },
};

export async function renderGiftPromo({ reward, format }) {
  const P = GIFT_L[format], { W, H } = P;
  const [c, ctx] = newCanvas(W, H);
  await document.fonts?.ready;
  const [photo, icon] = await Promise.all([loadImg(reward.image, true), iconImg(Gift, "#E11D48")]);
  drawBackground(ctx, W, H);
  await drawBrand(ctx, W, P.logo, format === "story" ? 1.2 : 1);
  text(ctx, "JETZT IM PRÄMIENSHOP", W / 2, P.label, { size: format === "story" ? 40 : 30, weight: 800, font: "Plus Jakarta Sans", color: "rgba(255,255,255,0.9)" });
  const [x, y, w, h] = P.card;
  whiteCard(ctx, x, y, w, h, 56);
  ctx.save(); roundRect(ctx, x + 20, y + 20, w - 40, h - 40, 40); ctx.clip();
  if (photo) { const s = Math.max((w - 40) / photo.width, (h - 40) / photo.height); ctx.drawImage(photo, x + w / 2 - (photo.width * s) / 2, y + h / 2 - (photo.height * s) / 2, photo.width * s, photo.height * s); }
  else { ctx.fillStyle = "#FFE4EC"; ctx.fillRect(x, y, w, h); if (icon) ctx.drawImage(icon, x + w / 2 - w * 0.2, y + h / 2 - w * 0.2, w * 0.4, w * 0.4); }
  ctx.restore();
  text(ctx, reward.title, W / 2, P.title, { size: format === "story" ? 76 : 54, weight: 800, maxW: W - 120, fit: true });
  pricePill(ctx, W / 2, P.price, `${Number(reward.cost).toLocaleString("de-DE")} Punkte`, format === "story" ? 64 : 44);
  await drawCta(ctx, W, P.cta[0], P.cta[1], { code: "", title: "Punkte sammeln & Geschenk holen" });
  return toBlob(c);
}

const MEMBER_L = {
  story: { W: 1080, H: 1920, logo: 150, avatar: 560, r: 190, name: 870, line: 950, card: [140, 1040, 800, 420], cta: [1620, 240] },
  square: { W: 1080, H: 1080, logo: 80, avatar: 300, r: 120, name: 490, line: 545, card: [190, 590, 700, 250], cta: [880, 160] },
};

export async function renderMemberPromo({ user, format }) {
  const P = MEMBER_L[format], { W, H } = P, story = format === "story";
  const [c, ctx] = newCanvas(W, H);
  await document.fonts?.ready;
  const avatar = await loadImg(user.picture, true);
  drawBackground(ctx, W, H);
  await drawBrand(ctx, W, P.logo, story ? 1.2 : 1);
  [1.45, 1.22].forEach((m, i) => { ctx.beginPath(); ctx.arc(W / 2, P.avatar, P.r * m, 0, Math.PI * 2); ctx.fillStyle = `rgba(255,255,255,${i ? 0.2 : 0.1})`; ctx.fill(); });
  drawAvatar(ctx, avatar, user.name, W / 2, P.avatar, P.r);
  text(ctx, user.name, W / 2, P.name, { size: story ? 80 : 56, weight: 800, maxW: W - 140, fit: true });
  text(ctx, "lädt dich in die itzPlay Community ein 💖", W / 2, P.line, { size: story ? 42 : 32, weight: 600, font: "Plus Jakarta Sans", color: "rgba(255,255,255,0.92)", maxW: W - 120, fit: true });
  const [x, y, w, h] = P.card;
  whiteCard(ctx, x, y, w, h, 48);
  text(ctx, "MEIN EINLADUNGSCODE", W / 2, y + h * 0.24, { size: story ? 34 : 26, weight: 800, font: "Plus Jakarta Sans", color: "#E11D48" });
  text(ctx, user.referral_code, W / 2, y + h * 0.56, { size: story ? 120 : 80, weight: 900, color: "#1E1B1D" });
  text(ctx, "Videos schauen · Punkte sammeln · Geschenke holen", W / 2, y + h * 0.82, { size: story ? 30 : 24, weight: 600, font: "Plus Jakarta Sans", color: "#64748B", maxW: w - 60, fit: true });
  await drawCta(ctx, W, P.cta[0], P.cta[1], { code: user.referral_code, title: "Kostenlos mitmachen mit meinem Code" });
  return toBlob(c);
}
